import useAccountInfo from "@/hooks/useAccountInfo";
import { api } from "@/lib/api-client";
import { useSidebarStore } from "@/stores/useSidebarStore";
import { canAccessClientBilling } from "@/utils/billing-access";
import { cn } from "@/utils";
import { Button } from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { CreditCard } from "lucide-react";
import { useEffect, useRef } from "react";
import {
  trackBillingBadgeClicked,
  trackBillingBadgeShown,
} from "@/utils/billing-tracking";
import { formatUnreadCount } from "./formatUnreadCount";
import { useSidebarNavigate } from "./useSidebarNavigate";

export const BillingButton = () => {
  const sidebarOpen = useSidebarStore((state) => state.isOpen);
  const accountInfo = useAccountInfo((state) => state.accountInfo);
  const navigateFromSidebar = useSidebarNavigate();

  const canSeeBilling = canAccessClientBilling(accountInfo);

  // Lime invoices only — the eracuni-issued ones need a slow round-trip that
  // does not belong on an always-mounted sidebar button.
  const { data } = api.billing.useGetClientBilling({ enabled: canSeeBilling });

  const unpaidInvoices = (data?.invoices ?? []).filter(
    (invoice) => invoice.status === "open" || invoice.status === "overdue",
  );
  const hasUnpaid = unpaidInvoices.length > 0;
  const hasOverdue = unpaidInvoices.some(
    (invoice) => invoice.status === "overdue",
  );
  const unpaidCount = unpaidInvoices.length;

  // The invoices arrive after mount, so the impression fires when the badge
  // first becomes visible rather than on mount; the ref keeps it to one.
  const impressionTracked = useRef(false);
  useEffect(() => {
    if (impressionTracked.current || !canSeeBilling || !hasUnpaid) return;
    impressionTracked.current = true;
    trackBillingBadgeShown({ unpaidCount, hasOverdue });
  }, [canSeeBilling, hasUnpaid, hasOverdue, unpaidCount]);

  if (!canSeeBilling) {
    return null;
  }

  return (
    <Button
      data-identifier="billing-button"
      className={cn(
        "w-full shrink-0 justify-start rounded-4xl px-3 text-gray-500",
        "transition-all duration-150",
        { "rounded-3xl": sidebarOpen },
      )}
      variant="light"
      isIconOnly={!sidebarOpen}
      size="sm"
      aria-label={t`Računi in naročnina`}
      onPress={() => {
        trackBillingBadgeClicked({ unpaidCount, hasOverdue });
        navigateFromSidebar("/dashboard/billing");
      }}
    >
      <div className="flex w-full items-center gap-2">
        <div className="relative">
          <CreditCard size={16} className="-ml-0.5 shrink-0" />

          {hasUnpaid && (
            <span
              className={cn("absolute -top-1 -right-1 size-2 rounded-full", {
                "bg-danger animate-pulse": hasOverdue,
                "bg-warning-500": !hasOverdue,
              })}
              aria-hidden
            />
          )}
        </div>

        {sidebarOpen && (
          <>
            <span className="text-xs font-medium text-gray-500">
              <Trans>Računi in naročnina</Trans>
            </span>

            {hasUnpaid && (
              <span
                className={cn(
                  "ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10px] leading-none font-semibold",
                  {
                    "bg-danger text-white": hasOverdue,
                    "bg-warning-100 text-warning-700": !hasOverdue,
                  },
                )}
              >
                {formatUnreadCount(unpaidCount)}
              </span>
            )}
          </>
        )}
      </div>
    </Button>
  );
};
